
import { StreamChunkData } from '@app-libs/model';

export class StreamingUtils {
  /**
   * Languages we want to detect as code blocks.
   * If you also want to detect "```" with *no* language, you can adapt your regex to allow ''.
   */
  static supportedLanguages: string[] = ['mermaid'];

  /**
   * Enhanced streaming processor that handles both text content and tool events.
   * Uses extractors that return StreamChunkData for consistent handling across providers.
   * 
   * @param stream - The async iterable stream from provider SDK
   * @param observer - RxJS observer to emit chunks to
   * @param chunkExtractor - Function to extract StreamChunkData from provider-specific chunks
   */
  static processStream(
    stream: any, 
    observer: any, 
    chunkExtractor?: (chunk: any) => StreamChunkData | undefined
  ): void {
    (async () => {
      let last_chunk;
      for await (const event of stream) {
        last_chunk = event;

        // Use custom extractor if provided, otherwise use default text extraction
        const chunkData: StreamChunkData | undefined = chunkExtractor 
          ? chunkExtractor(event)
          : this.extractDefaultChunk(event);

        if (!chunkData) {
          continue;
        }

        // Emit the chunk
        observer.next(chunkData);
      }

      // Emit the final message
      const final_chunk: StreamChunkData = {
        content: '',
        type: 'final',
        data: last_chunk,
      };

      // Remove instructions from the last chunk because of the size
      if (final_chunk.data?.data?.instructions) {
        final_chunk.data.data.instructions = '';
      }
      observer.next(final_chunk);

      // Signal completion
      observer.complete();
    })().catch((error) => {
      observer.error(error);
    });
  }

  /**
   * Extract content from a chunk in your OpenAI (or other) streaming format.
   * Legacy method for backward compatibility.
   */
  static extractContent(event: any): string | undefined {
    // Cater for completions and assistants response type
    const data =
      event?.choices?.[0]?.delta?.content ||
      event?.data?.delta?.content?.[0]?.text?.value ||
      undefined;
    return data;
  }

  /**
   * Default chunk extractor that returns StreamChunkData.
   * Used when no custom extractor is provided.
   */
  static extractDefaultChunk(event: any): StreamChunkData | undefined {
    const content = this.extractContent(event);
    if (content) {
      return {
        content,
        type: 'text'
      };
    }
    return undefined;
  }

  /**
   * This tries to detect a code block start of the form:
   *    ```LANG
   * where LANG is one of the configured `supportedLanguages`.
   * Return null if no recognized language is found.
   */
  static detectCodeBlockStart(content: string): string | null {
    // Build a single pattern like: ```(mermaid|python)
    const pattern = new RegExp(
      '```(' + this.supportedLanguages.join('|') + ')',
      'i', // case-insensitive, can remove if you want case-sensitive
    );
    const match = content.match(pattern);
    if (match) {
      return match[1]; // the language
    }
    return null;
  }

  /**
   * Detect a code block end (i.e., a line that contains triple backticks).
   */
  static detectCodeBlockEnd(content: string): boolean {
    // If you want to confirm that the line is exactly "```" on a line by itself, use /^```$/.
    // But often "```" can appear anywhere in that line to signal the end.
    return content.includes('```');
  }

  /*
   * Extracts chunks from a streaming response.
   */
  static extractChunks(response: string, chunkSeparator = '\n\n'): StreamChunkData[] {
    const chunks: StreamChunkData[] = [];
    const trimmedResponse = response.trim();
    if (trimmedResponse === '') {
      return chunks;
    }

    // Split in case we get multiple events at the same time
    const lines = trimmedResponse.split(chunkSeparator);
    for (const line of lines) {
      if (!line || line.trim() === '') {
        continue; // Skip empty lines
      }
      try {
        const chunk = JSON.parse(line) as StreamChunkData;
        chunks.push(chunk);
      } catch (error) {
        console.error('Failed to parse chunk:', line, error);
      }
    }

    return chunks;
  }
}